import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { isEqual } from "lodash";
import UtilityService from "app/data/services/UtilityService";
import {
  VAT_MESSAGE_KEYS,
  getLocalVatError,
  isVatUnchanged,
  mapValidateResponse,
  normalizeCountryCode,
  normalizeVat
} from "app/data/helpers/vatHelpers";
import { IVatCheckStatus, VatCheckState } from "app/data/types/vatTypes";

export const VAT_CHECK_DEBOUNCE_MS = 500;

// Answers that stay valid for the same value and country. `unavailable` is
// retried when the user leaves the field again.
const REUSABLE_ANSWERS: ReadonlySet<VatCheckState> = new Set<VatCheckState>([
  "valid",
  "invalid",
  "unsupported",
  "malformed",
  "mismatch"
]);

interface UseVatValidationOptions {
  value?: string | null;
  countryCode?: string | null;
  // VAT number and billing country loaded with the account.
  initialValue?: string | null;
  initialCountryCode?: string | null;
  // Formik `status` and `setStatus`; the state is published in `status.vat`.
  status?: Record<string, unknown> | null;
  setStatus?: (status: Record<string, unknown>) => void;
  // The activation portal has no session: call the public endpoint without a
  // token.
  anonymous?: boolean;
}

export const useVatValidation = ({
  value,
  countryCode,
  initialValue,
  initialCountryCode,
  status,
  setStatus,
  anonymous = false
}: UseVatValidationOptions) => {
  const vatNumber = normalizeVat(value);
  const country = normalizeCountryCode(countryCode);

  // Outcome that needs no request; null when the endpoint must be asked.
  const localStatus = useMemo((): IVatCheckStatus | null => {
    const input = {
      message: null,
      checkedValue: vatNumber,
      checkedCountry: country
    };

    if (!vatNumber || !country) return { state: "not-checked", ...input };

    const localError = getLocalVatError(country, vatNumber);
    if (localError) return { state: localError, ...input };

    const unchanged = isVatUnchanged({
      value: vatNumber,
      countryCode: country,
      initialValue,
      initialCountryCode
    });
    return unchanged ? { state: "unchanged", ...input } : null;
  }, [vatNumber, country, initialValue, initialCountryCode]);

  const needsCheck = localStatus === null;
  const [answer, setAnswer] = useState<IVatCheckStatus | null>(null);

  const timer = useRef<ReturnType<typeof setTimeout>>();
  const request = useRef<AbortController>();
  const runId = useRef(0);
  const latest = useRef({ vatNumber, country, needsCheck, answer, anonymous });
  latest.current = { vatNumber, country, needsCheck, answer, anonymous };

  const cancel = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = undefined;
    request.current?.abort();
    request.current = undefined;
    runId.current += 1;
  }, []);

  const run = useCallback(() => {
    cancel();

    const { vatNumber, country, needsCheck, anonymous } = latest.current;
    if (!needsCheck || !country) return;

    const id = runId.current;
    const controller = new AbortController();
    const context = { countryCode: country, vatNumber };
    const checked = { checkedValue: vatNumber, checkedCountry: country };

    request.current = controller;
    setAnswer({
      state: "checking",
      message: VAT_MESSAGE_KEYS.checking,
      ...checked
    });

    UtilityService.validateVat(context, controller.signal, { anonymous })
      .then(
        (response) =>
          mapValidateResponse(response.status, response.data, context),
        (error) =>
          axios.isCancel(error)
            ? null
            : mapValidateResponse(undefined, undefined, context)
      )
      .then((result) => {
        if (!result || id !== runId.current) return;

        request.current = undefined;
        setAnswer({ ...result, ...checked });
      });
  }, [cancel]);

  // Check the value after the user stops typing, and again for a new country.
  useEffect(() => {
    cancel();
    if (!needsCheck) return;

    const { answer } = latest.current;
    const answered =
      answer?.checkedValue === vatNumber &&
      answer.checkedCountry === country &&
      REUSABLE_ANSWERS.has(answer.state);

    if (!answered) {
      timer.current = setTimeout(run, VAT_CHECK_DEBOUNCE_MS);
    }
  }, [vatNumber, country, needsCheck, cancel, run]);

  useEffect(() => cancel, [cancel]);

  // Bound to the field's blur: check now instead of waiting for the debounce,
  // or retry an unavailable answer.
  const flush = useCallback(() => {
    const { vatNumber, country, needsCheck, answer } = latest.current;
    if (!needsCheck) return;

    const retry =
      answer?.state === "unavailable" &&
      answer.checkedValue === vatNumber &&
      answer.checkedCountry === country;

    if (timer.current !== undefined || retry) run();
  }, [run]);

  const vatStatus = useMemo((): IVatCheckStatus => {
    if (localStatus) return localStatus;

    if (
      answer?.checkedValue === vatNumber &&
      answer.checkedCountry === country
    ) {
      return answer;
    }

    return {
      state: "pending",
      message: null,
      checkedValue: vatNumber,
      checkedCountry: country
    };
  }, [localStatus, answer, vatNumber, country]);

  // Formik replaces `status` on reset, so publish again whenever it differs.
  useEffect(() => {
    if (!setStatus || isEqual(status?.vat, vatStatus)) return;
    setStatus({ ...(status ?? {}), vat: vatStatus });
  }, [status, setStatus, vatStatus]);

  return { status: vatStatus, flush };
};
